import classnames from "classnames";
import PropTypes from "prop-types";
import React from "react";

import useOutputReportStore from "../../../store/OutputReport.store";
import {
  formatLabel,
  getOldReportBenchmark,
  getOutputType,
  getProfit,
  getReportBenchmark,
} from "../../../utils/helpers/dataTable";
import DataTableRow from "../DataTableRow";

const DataTableRows = ({
  name,
  variation,
  tableRowKeys,
  tableColumns,
  tableDataTypes,
  scenarioColumns,
  highlightColumns,
  formatCellDataBy,
  hasProfitLabel,
}) => {
  const data = useOutputReportStore((state) => state.data);
  const { reportType, assetType } = getOutputType(data);

  const processedRows = tableRowKeys.map((row) => {
    const reportBenchmark =
      assetType === "equity"
        ? getOldReportBenchmark(tableColumns)
        : getReportBenchmark(tableColumns);
    const tableColumnHeaders = tableColumns.map((col) => {
      const formatType =
        formatCellDataBy === "row"
          ? tableDataTypes[row.key]
          : tableDataTypes[col.columnTitle];
      const labelValue = formatLabel(formatType, col[row.key]);
      const profit =
        hasProfitLabel && !col.columnTitle.includes("Benchmark")
          ? getProfit(reportBenchmark[row.key], col[row.key], formatType)
          : null;

      return {
        label: labelValue,
        isCellHighlighted: highlightColumns.includes(col.columnTitle),
        isCellScenario: scenarioColumns.includes(col.columnTitle),
        profit:
          hasProfitLabel &&
          !row.key.includes("Benchmark") &&
          !row.key.includes("Total")
            ? profit
            : null,
      };
    });

    return [
      // row header cell
      {
        label: row.label,
        isRowHighlighted:
          variation !== "basic" &&
          ["Yield to Worst", "Tracking Error to Benchmark"].includes(row.key),
        isRowHeader: row.isHeader,
      },
      // rest of row cells
      ...tableColumnHeaders,
    ];
  });

  const isSingleCol = tableColumns.length === 1;
  const isDoubleCol = tableColumns.length === 2;

  return (
    <>
      {processedRows.map((rows, rowIdx) => {
        let rowHighlightedIndex = null;

        rows.forEach((row, i) => {
          if (row.isRowHighlighted && name !== "portfolioCharacteristics-os") {
            rowHighlightedIndex = rowIdx;
          }
        });

        return (
          <tr
            key={rowIdx}
            className={classnames("dataTable__row", {
              "dataTable__row--odd":
                (rowIdx + 1) % 2 && rowIdx !== rowHighlightedIndex,
              "dataTable__row--highlighted": rowIdx === rowHighlightedIndex,
              "dataTable__row--total": rows[0].label === "Total",
              "dataTable__row--header": rows[0].isRowHeader,
              "dataTable__col--single": isSingleCol,
              "dataTable__col--double": isDoubleCol,
            })}
          >
            <DataTableRow
              name={name}
              rows={rows}
              rowIndex={rowIdx}
              assetType={assetType}
              reportType={reportType}
            />
          </tr>
        );
      })}
    </>
  );
};

DataTableRows.propTypes = {
  name: PropTypes.string,
  variation: PropTypes.string,
  tableRowKeys: PropTypes.array,
  tableColumns: PropTypes.array,
  tableDataTypes: PropTypes.object,
  scenarioColumns: PropTypes.array,
  highlightColumns: PropTypes.array,
  formatCellDataBy: PropTypes.oneOf(["row", "column"]),
  hasProfitLabel: PropTypes.bool,
};

DataTableRows.defaultProps = {
  variation: "basic",
  tableRowKeys: [],
  tableColumns: [],
  tableDataTypes: {},
  scenarioColumns: [],
  highlightColumns: [],
  formatCellDataBy: "row",
  hasProfitLabel: false,
};

export default DataTableRows;
